import { Controller } from "stimulus";

type Campaign = { id: string; name: string; company: string; archived: boolean; active: boolean };

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 250;
const SCROLL_THRESHOLD_PX = 48;

const escapeHtml = (value: string): string =>
  value.replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char] || char);

export default class extends Controller {
  static targets = ["trigger", "label", "search", "options", "status"];
  static values = { url: String, placeholder: String, allLabel: String, selectedId: String };

  declare readonly triggerTarget: HTMLButtonElement;
  declare readonly labelTarget: HTMLElement;
  declare readonly searchTarget: HTMLInputElement;
  declare readonly optionsTarget: HTMLElement;
  declare readonly statusTarget: HTMLElement;
  declare urlValue: string;
  declare placeholderValue: string;
  declare allLabelValue: string;
  declare selectedIdValue: string;

  private campaigns: Campaign[] = [];
  private page = 0;
  private query = "";
  private hasMore = true;
  private loading = false;
  private seq = 0;
  private loadedOnce = false;
  private focusIndex = -1;
  private timer?: number;

  private readonly onDocumentClick = (event: MouseEvent): void => {
    if (!this.element.contains(event.target as Node)) this.close();
  };

  connect(): void {
    document.addEventListener("click", this.onDocumentClick);
  }

  disconnect(): void {
    document.removeEventListener("click", this.onDocumentClick);
    window.clearTimeout(this.timer);
  }

  toggle(event: Event): void {
    event.preventDefault();
    if (this.element.classList.contains("open")) this.close();
    else this.open();
  }

  search(): void {
    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => {
      const value = this.searchTarget.value.trim();
      if (value === this.query) return;
      this.query = value;
      this.reload();
    }, SEARCH_DEBOUNCE_MS);
  }

  scroll(): void {
    const { scrollTop, scrollHeight, clientHeight } = this.optionsTarget;
    if (scrollTop + clientHeight >= scrollHeight - SCROLL_THRESHOLD_PX) this.fetchPage(false);
  }

  choose(event: Event): void {
    const option = (event.target as HTMLElement).closest<HTMLElement>("[data-option-id]");
    if (option) this.pick(option.dataset.optionId || "", option.dataset.optionName || "");
  }

  keydown(event: KeyboardEvent): void {
    if (event.key === "Escape") {
      this.close();
      this.triggerTarget.focus();
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp" && event.key !== "Enter") return;
    if (!this.element.classList.contains("open")) {
      if (event.key !== "Enter") this.open();
      return;
    }

    const options = this.optionElements();
    if (event.key === "Enter") {
      const option = options[this.focusIndex];
      if (option) {
        event.preventDefault();
        this.pick(option.dataset.optionId || "", option.dataset.optionName || "");
      }
      return;
    }

    event.preventDefault();
    const step = event.key === "ArrowDown" ? 1 : -1;
    this.focusIndex = Math.max(0, Math.min(options.length - 1, this.focusIndex + step));
    options.forEach((option, index) => option.classList.toggle("is-focused", index === this.focusIndex));
    options[this.focusIndex]?.scrollIntoView({ block: "nearest" });
    if (step > 0 && this.focusIndex >= options.length - 2) this.fetchPage(false);
  }

  private open(): void {
    document.querySelectorAll(".tt-campaign-picker.open").forEach((picker) => {
      if (picker !== this.element) picker.classList.remove("open");
    });
    this.element.classList.add("open");
    this.triggerTarget.setAttribute("aria-expanded", "true");
    if (!this.loadedOnce) {
      this.loadedOnce = true;
      this.reload();
    }
    this.searchTarget.focus();
  }

  private close(): void {
    if (!this.element.classList.contains("open")) return;
    this.element.classList.remove("open");
    this.triggerTarget.setAttribute("aria-expanded", "false");
  }

  private pick(id: string, name: string): void {
    this.selectedIdValue = id;
    this.labelTarget.textContent = name || this.placeholderValue;
    this.element.classList.toggle("has-val", !!id || !!this.allLabelValue);
    this.render();
    this.close();
    this.element.dispatchEvent(new CustomEvent("testing-campaign-picker:picked", { detail: { id, name }, bubbles: true }));
  }

  private reload(): void {
    this.campaigns = [];
    this.page = 0;
    this.hasMore = true;
    this.focusIndex = -1;
    this.loading = false;
    this.fetchPage(true);
  }

  private async fetchPage(reset: boolean): Promise<void> {
    if (this.loading || (!reset && !this.hasMore)) return;
    this.loading = true;
    const requestSeq = ++this.seq;
    const nextPage = this.page + 1;
    this.setStatus("Loading…");

    const url = new URL(this.urlValue, window.location.origin);
    url.searchParams.set("page", String(nextPage));
    if (this.query) url.searchParams.set("q", this.query);

    try {
      const response = await fetch(url.toString(), { headers: { Accept: "application/json" }, credentials: "same-origin" });
      if (!response.ok) throw new Error(String(response.status));
      const campaigns: Campaign[] = await response.json();
      if (requestSeq !== this.seq) return;
      this.campaigns = this.campaigns.concat(campaigns);
      this.page = nextPage;
      this.hasMore = campaigns.length === PAGE_SIZE;
      this.loading = false;
      this.render();
      if (this.hasMore && this.optionsTarget.scrollHeight <= this.optionsTarget.clientHeight) this.fetchPage(false);
    } catch {
      if (requestSeq !== this.seq) return;
      this.loading = false;
      this.hasMore = false;
      this.setStatus("Couldn't load campaigns. Close and open the list to try again.");
      this.loadedOnce = false;
    }
  }

  private render(): void {
    const allOption = this.allLabelValue && !this.query ? this.optionHtml("", this.allLabelValue, "") : "";
    const options = this.campaigns.map((campaign) =>
      this.optionHtml(campaign.id, campaign.name, [campaign.company, campaign.active ? "" : "paused"].filter(Boolean).join(" · "))
    );
    this.optionsTarget.innerHTML = allOption + options.join("");
    this.focusIndex = -1;
    if (!allOption && !options.length) this.setStatus(this.loading ? "Loading…" : "No campaigns match.");
    else this.setStatus("");
  }

  private optionHtml(id: string, name: string, meta: string): string {
    const active = id === this.selectedIdValue ? " active" : "";
    return `<button type="button" class="dd-opt${active}" role="option" aria-selected="${!!active}" data-option-id="${escapeHtml(id)}" data-option-name="${escapeHtml(name)}">` +
      `<span class="dd-opt-label">${escapeHtml(name)}</span>${meta ? `<span class="tt-opt-meta">${escapeHtml(meta)}</span>` : ""}</button>`;
  }

  private optionElements(): HTMLElement[] {
    return Array.from(this.optionsTarget.querySelectorAll<HTMLElement>("[data-option-id]"));
  }

  private setStatus(message: string): void {
    this.statusTarget.textContent = message;
    this.statusTarget.hidden = !message;
  }
}
